import { useCallback, useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useMarketData } from '../../hooks/useMarketData';
import { useEditorModeContext } from '../../context/EditorModeContext.utils';
import { ScaledMarkerLayer } from './ScaledMarkerLayer';
import { MapImageOverlay } from './MapImageOverlay';
import { AddPositionHandler } from './AddPositionHandler';
import { ErrorBoundary } from '../primitives/ErrorBoundary';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { computeFocusPanOffset, type FocusTarget } from './focus';
import { coordinatesBounds } from '../../lib/map/coords';
import { MAP_CONFIG } from './mapConfig';
import styles from './MapView.module.css';

export type { FocusTarget } from './focus';

// Local fallback when Leaflet (or any of the marker/overlay layers below)
// throws during render. Belt-and-suspenders alongside the app-level
// boundary in main.tsx — keeps the List / My Visit / Info tabs usable
// when the map alone has a problem.
function MapFailureFallback({ error }: { error: Error }) {
  const [showDetails, setShowDetails] = useState(false);
  const handleResetPWA = async () => {
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map((r) => r.unregister()));
      }
      if ('caches' in window) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch {
      // best-effort; reload anyway
    }
    window.location.reload();
  };
  return (
    <div role="alert" className={`${styles.mapView} ${styles.failureRoot} map-view`}>
      <div className={styles.failureBody}>
        <p className={styles.failureHeading}>The map failed to load.</p>
        <div className={styles.failureActions}>
          <button type="button" onClick={() => window.location.reload()}>
            Reload
          </button>
          <button type="button" onClick={handleResetPWA}>
            Clear cache &amp; reload
          </button>
          <button type="button" onClick={() => setShowDetails((v) => !v)}>
            {showDetails ? 'Hide' : 'Show'} details
          </button>
        </div>
        {showDetails && (
          <pre className={styles.failureDetails}>
            {error.message}
            {error.stack ? '\n\n' + error.stack.split('\n').slice(0, 6).join('\n') : ''}
          </pre>
        )}
      </div>
    </div>
  );
}

const FOCUS_ZOOM_DESKTOP = 1.0;
const FOCUS_ZOOM_MOBILE = 0.5;
// Viewport padding (px) around the marker extent for the initial fitBounds.
const INITIAL_FIT_PADDING_PX = 48;

interface Props {
  /** UID of the currently-selected vendor (for marker highlight). */
  selectedVendorUid?: string | null | undefined;
  /** Fires when a vendor marker is tapped. */
  onVendorTap?: ((vendorUid: string) => void) | undefined;
  /**
   * Imperatively pan/zoom to this target. Reset to `null` after the pan
   * to allow re-focusing the same vendor again.
   */
  focusTarget?: FocusTarget | null | undefined;
  /** Called when the selected vendor falls out of the filtered set. */
  onSelectionLost?: (() => void) | undefined;
  /**
   * Fires when the user taps the empty map area (not a marker, not in
   * edit mode). AppShell uses this to dismiss the mobile bottom sheet.
   */
  onMapTap?: (() => void) | undefined;
  /**
   * Pixels of viewport obscured at the bottom (mobile bottom sheet).
   * When non-zero: maxBounds is extended south so bottom markers can be
   * panned into view, and focus pans offset upward so the focused vendor
   * lands in the visible area above the sheet.
   */
  bottomInsetPx?: number | undefined;
}

export function MapView(props: Props) {
  // Scoped boundary: a thrown Leaflet/marker error here renders the map
  // fallback while the rest of AppShell (tabs, list, info) keeps running.
  return (
    <ErrorBoundary fallback={({ error }) => <MapFailureFallback error={error} />}>
      <MapViewInner {...props} />
    </ErrorBoundary>
  );
}

function MapViewInner({
  selectedVendorUid = null,
  onVendorTap,
  focusTarget = null,
  onSelectionLost,
  onMapTap,
  bottomInsetPx = 0,
}: Props) {
  const { positions } = useMarketData();

  const mapRef = useRef<HTMLDivElement>(null);
  // State (not ref) so children re-render once Leaflet is initialized.
  // Avoids the react-hooks/refs error from reading mapInstanceRef.current
  // during render.
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const { isEditing } = useEditorModeContext();
  const isDesktop = useIsDesktop();
  // Snapshot the initial viewport so the first mount picks the right
  // default zoom; we intentionally don't re-zoom on breakpoint changes
  // because the user's panning/zooming would get wiped out.
  const initialDefaultZoomRef = useRef(
    isDesktop ? MAP_CONFIG.defaultZoomDesktop : MAP_CONFIG.defaultZoomMobile,
  );

  // Keep a stable ref to the latest onMapTap so the Leaflet click
  // listener can be registered once without re-subscribing on every
  // parent re-render.
  const onMapTapRef = useRef(onMapTap);
  useEffect(() => {
    onMapTapRef.current = onMapTap;
  }, [onMapTap]);

  // Callback ref captures the container div on mount. Pairs with the
  // useEffect below which actually instantiates Leaflet — separated so we
  // never read a ref during render.
  const setContainerRef = useCallback((node: HTMLDivElement | null) => {
    mapRef.current = node;
  }, []);

  // Initialize Leaflet exactly once per mount. Deps MUST be [] — depending
  // on `mapInstance` causes the cleanup to run immediately after `setMapInstance`
  // commits, which calls `map.remove()` and leaves the child layers (image
  // overlay, markers) pointing at a map whose panes have been destroyed.
  // That produced "this.getPane() is undefined" in onAdd when Leaflet's
  // deferred `whenReady` fired after the teardown.
  useEffect(() => {
    const node = mapRef.current;
    if (!node) return;

    const map = L.map(node, {
      crs: L.CRS.Simple,
      minZoom: MAP_CONFIG.minZoom,
      maxZoom: MAP_CONFIG.maxZoom,
      zoomControl: false,
      attributionControl: false,
      maxBoundsViscosity: 1.0,
      // Mobile pinch is continuous; a tiny snap (0.1) is invisible to the
      // finger but cuts how many discrete zoom states fire per gesture,
      // which matters because each one re-projects every marker. Desktop
      // gets 0.25 — fine enough to not feel sluggish, snapped enough to
      // keep discrete mouse-wheel ticks stable.
      zoomSnap: isDesktop ? 0.25 : 0.1,
    });

    map.setView(MAP_CONFIG.defaultCenter, initialDefaultZoomRef.current);

    L.control.zoom({ position: 'bottomright' }).addTo(map);

    // Initial maxBounds; the bottom edge is reshaped in a separate effect
    // below when a bottom sheet is visible.
    const pad = 0.1;
    const maxBounds: L.LatLngBoundsExpression = [
      [-MAP_CONFIG.height * pad, -MAP_CONFIG.width * pad],
      [MAP_CONFIG.height * (1 + pad), MAP_CONFIG.width * (1 + pad)],
    ];
    map.setMaxBounds(maxBounds);

    setMapInstance(map);

    return () => {
      map.remove();
      setMapInstance(null);
    };
  }, []);

  // One-shot initial view: once positions load, fit the view to their
  // actual bounding box instead of trusting the hand-tuned defaultCenter
  // (which drifts when the map image or layout changes — issue #218).
  // `setView(defaultCenter)` above remains the fallback while loading or
  // when no positions exist. Fires at most once per mount — background
  // revalidation or filter changes must not yank the view around — and
  // is skipped entirely when the page loaded with a `/listing/<uid>`
  // deep link, where useDeepLinkFocus owns the initial pan.
  const hadInitialDeepLinkRef = useRef(selectedVendorUid != null);
  const didInitialFitRef = useRef(false);
  useEffect(() => {
    if (!mapInstance || didInitialFitRef.current || hadInitialDeepLinkRef.current) return;
    const bounds = coordinatesBounds(
      positions.filter((p) => p.type === 'vendor').map((p) => p.coordinates),
    );
    if (!bounds) return;
    didInitialFitRef.current = true;
    mapInstance.fitBounds(bounds, {
      padding: L.point(INITIAL_FIT_PADDING_PX, INITIAL_FIT_PADDING_PX),
      maxZoom: MAP_CONFIG.initialFitMaxZoom,
      animate: false,
    });
  }, [mapInstance, positions]);

  // Re-apply maxBounds when the bottom inset changes. Note on coordinates:
  // Leaflet CRS.Simple + MapImageOverlay BOUNDS [[0,0],[height,width]] puts
  // lat 0 at the BOTTOM of the image and lat `height` at the TOP. So
  // extending "south" (giving room to pan bottom-of-map markers ABOVE a
  // sheet) means making the SW corner's lat MORE NEGATIVE, not extending
  // the NE corner.
  useEffect(() => {
    if (!mapInstance) return;
    const pad = 0.1;
    // Just enough slack to scroll bottom-of-image markers out from under the
    // sheet. `bottomInsetPx` is pixels; at the default zoom (-2) one CSS
    // pixel equals 4 lat units, so `× 4` covers that case. The `+ 80`
    // buffer keeps markers a little above the sheet top rather than
    // flush against it.
    const southSlack = bottomInsetPx > 0 ? bottomInsetPx * 4 + 80 : 0;
    const maxBounds: L.LatLngBoundsExpression = [
      // southWest: lower-left corner (lat decreases going south)
      [-MAP_CONFIG.height * pad - southSlack, -MAP_CONFIG.width * pad],
      // northEast: upper-right corner (unchanged)
      [MAP_CONFIG.height * (1 + pad), MAP_CONFIG.width * (1 + pad)],
    ];
    mapInstance.setMaxBounds(maxBounds);
  }, [mapInstance, bottomInsetPx]);

  // When the container resizes (desktop right panel opens/closes, browser
  // resize, orientation change), tell Leaflet to recompute its viewport.
  // Without this, marker projection and pan targets use stale dimensions.
  useEffect(() => {
    if (!mapInstance) return;
    const container = mapInstance.getContainer();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      // `pan: false` keeps the top-left origin stable instead of re-
      // centering. Without this the map visibly shifts left when the
      // desktop right panel opens, because Leaflet would recenter the
      // geographic midpoint in the shrunken viewport.
      mapInstance.invalidateSize({ animate: false, pan: false });
    });
    ro.observe(container);
    return () => {
      ro.disconnect();
    };
  }, [mapInstance]);

  // Imperative pan/zoom driven by the `focusTarget` prop. Bumping
  // `focusTarget.key` re-fires the pan even for the same vendor.
  // The ref guards against stale re-application: `bottomInsetPx` /
  // `isDesktop` are deps because the pan math reads them, but a change
  // in either one without a new focus key (e.g. the bottom sheet
  // opening when the user taps a different marker) must NOT re-pan to
  // the previous vendor. See issue #126.
  const lastAppliedFocusKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!mapInstance || !focusTarget) return;
    if (lastAppliedFocusKeyRef.current === focusTarget.key) return;
    lastAppliedFocusKeyRef.current = focusTarget.key;
    const target = L.latLng(focusTarget.coordinates[0], focusTarget.coordinates[1]);
    const focusZoom = isDesktop ? FOCUS_ZOOM_DESKTOP : FOCUS_ZOOM_MOBILE;
    const targetZoom = Math.min(focusZoom, MAP_CONFIG.maxZoom);
    const edgePad = 40;

    const prefersReducedMotion =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Multi-position vendor with spread booths: fit all markers on screen.
    // Cap zoom at `focusZoom` so we don't over-zoom for very close booths.
    if (focusTarget.bounds) {
      mapInstance.fitBounds(focusTarget.bounds, {
        paddingTopLeft: L.point(edgePad, edgePad),
        paddingBottomRight: L.point(edgePad, edgePad + bottomInsetPx),
        maxZoom: targetZoom,
        animate: !prefersReducedMotion,
      });
      return;
    }

    // Skip the pan if the marker is already visible at a sufficient zoom.
    // Avoids needlessly animating the map when the user clicks a row for
    // a vendor whose marker is already on screen. `getSize()` reports the
    // Leaflet container size — on desktop we shrink that to the visible
    // area via CSS, so its right edge is the panel edge. On mobile the
    // sheet overlays the bottom of the container, so subtract that inset.
    const currentZoom = mapInstance.getZoom();
    const currentPoint = mapInstance.latLngToContainerPoint(target);
    const size = mapInstance.getSize();
    const visibleBottom = size.y - bottomInsetPx - edgePad;
    const inView =
      currentPoint.x >= edgePad &&
      currentPoint.x <= size.x - edgePad &&
      currentPoint.y >= edgePad &&
      currentPoint.y <= visibleBottom;
    const zoomOk = currentZoom >= targetZoom - 0.01;
    if (inView && zoomOk) return;

    const [totalOffsetX, totalOffsetY] = computeFocusPanOffset(
      focusTarget.labelPosition,
      bottomInsetPx,
    );
    let center = target;
    if (totalOffsetX !== 0 || totalOffsetY !== 0) {
      const point = mapInstance.project(target, targetZoom);
      const adjusted = point.add(L.point(totalOffsetX, totalOffsetY));
      center = mapInstance.unproject(adjusted, targetZoom);
    }
    mapInstance.setView(center, targetZoom, { animate: !prefersReducedMotion });
  }, [mapInstance, focusTarget, bottomInsetPx, isDesktop]);

  // Toggle the editor-mode class on the container as `isEditing` flips.
  useEffect(() => {
    if (!mapInstance) return;
    const container = mapInstance.getContainer();
    if (isEditing) {
      container.classList.add('editor-mode');
    } else {
      container.classList.remove('editor-mode');
    }
  }, [isEditing, mapInstance]);

  // Backdrop-tap-to-dismiss: in non-edit mode, a Leaflet click that
  // didn't originate on a marker fires onMapTap. AppShell uses this to
  // close the mobile bottom sheet. We key off `isEditing` so edit-mode
  // clicks (which drop new positions via AddPositionHandler) aren't
  // affected.
  useEffect(() => {
    if (!mapInstance || isEditing) return;
    function onClick(e: L.LeafletMouseEvent) {
      const target = e.originalEvent?.target as HTMLElement | undefined;
      // Marker DOM carries data-vendor-uid; ignore those so marker
      // taps still select (rather than clear) the vendor.
      if (target?.closest('[data-vendor-uid]')) return;
      onMapTapRef.current?.();
    }
    mapInstance.on('click', onClick);
    return () => {
      mapInstance.off('click', onClick);
    };
  }, [mapInstance, isEditing]);

  return (
    <div
      className={`${styles.mapView} map-view`}
      ref={setContainerRef}
      role="region"
      aria-label="Market map. Use the list tab for a vendor index."
      // The map is technically non-interactive at the wrapper level, but
      // we want keyboard users to be able to land on it so the screen
      // reader announces the region label and they can jump past it.
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
      tabIndex={0}
    >
      {/* Vendor markers are keyboard-focusable (role=button, aria-label) and
          activate on Enter/Space — see ScaledMarkerLayer. That replaces the
          former sr-only vendor button list, which added invisible tab stops
          for sighted keyboard users. */}
      {mapInstance && (
        <>
          <MapImageOverlay map={mapInstance} />
          <ScaledMarkerLayer
            map={mapInstance}
            selectedVendorUid={selectedVendorUid}
            focusKey={focusTarget?.key ?? null}
            onVendorTap={onVendorTap}
            onSelectionLost={onSelectionLost}
          />
          <AddPositionHandler map={mapInstance} />
        </>
      )}
    </div>
  );
}
